import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native-web';
import Card from '../components/Card';
import Button from '../components/Button';
import Badge from '../components/Badge';
import Modal from '../components/Modal';
import Input from '../components/Input';
import PageHeader from '../components/PageHeader';
import { colors, radius } from '../theme';
import { getScripts, createScript, updateScript, toggleScript, deleteScript } from '../api';

export default function ScriptsPage() {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingScript, setEditingScript] = useState(null);
  const [integrationModal, setIntegrationModal] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', allowedDomains: '' });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setScripts(await getScripts());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    const domains = form.allowedDomains
      .split(/[,\n]/)
      .map(d => d.trim())
      .filter(Boolean);
    const payload = {
      name: form.name,
      description: form.description,
      allowedDomains: domains,
    };
    if (editingScript) {
      await updateScript(editingScript.id, payload);
    } else {
      await createScript(payload);
    }
    setForm({ name: '', description: '', allowedDomains: '' });
    setEditingScript(null);
    setModalVisible(false);
    load();
  };

  const openEditModal = (s) => {
    setEditingScript(s);
    setForm({
      name: s.name,
      description: s.description || '',
      allowedDomains: (s.allowedDomains || []).join('\n'),
    });
    setModalVisible(true);
  };

  const openCreateModal = () => {
    setEditingScript(null);
    setForm({ name: '', description: '', allowedDomains: '' });
    setModalVisible(true);
  };

  const handleToggle = async (id) => { await toggleScript(id); load(); };
  const handleDelete = async (id) => {
    if (window.confirm('Delete this script and all its logs?')) {
      await deleteScript(id); load();
    }
  };

  const copyToClipboard = (text) => navigator.clipboard.writeText(text);

  return (
    <View>
      <PageHeader title="Scripts" subtitle="Each script = one landing page. Get integration code here.">
        <Button label="+ New Script" onPress={openCreateModal} />
      </PageHeader>

      {scripts.map((s) => (
        <Card key={s.id}>
          <View style={styles.cardHeader}>
            <View style={styles.cardTitleRow}>
              <Text style={styles.cardTitle}>{s.name}</Text>
              <Badge label={s.active ? 'Active' : 'Disabled'} color={s.active ? 'success' : 'muted'} />
            </View>
            {s.description && <Text style={styles.cardDesc}>{s.description}</Text>}
          </View>

          {/* Домены */}
          <View style={styles.domainsRow}>
            <Text style={styles.domainsLabel}>Allowed domains:</Text>
            {s.allowedDomains && s.allowedDomains.length > 0
              ? s.allowedDomains.map((d, i) => (
                  <Badge key={i} label={d} color="primary" />
                ))
              : <Text style={styles.anyDomain}>any domain</Text>
            }
          </View>

          {/* Статистика */}
          <View style={styles.statsRow}>
            <View style={styles.stat}>
              <Text style={styles.statValue}>{(s.totalEvents || 0).toLocaleString()}</Text>
              <Text style={styles.statLabel}>Total Events</Text>
            </View>
            <View style={styles.stat}>
              <Text style={styles.statValue}>{(s.eventsLast24h || 0).toLocaleString()}</Text>
              <Text style={styles.statLabel}>Last 24h</Text>
            </View>
          </View>

          <View style={styles.cardActions}>
            <Button label="Integration Code" icon="📋" variant="primary" size="sm" onPress={() => setIntegrationModal(s)} />
            <Button label="Edit" variant="ghost" size="sm" onPress={() => openEditModal(s)} />
            <Button label={s.active ? 'Disable' : 'Enable'} variant="ghost" size="sm" onPress={() => handleToggle(s.id)} />
            <Button label="Delete" variant="ghost" size="sm" onPress={() => handleDelete(s.id)} />
          </View>
        </Card>
      ))}

      {!loading && scripts.length === 0 && (
        <Card>
          <View style={styles.empty}>
            <Text style={styles.emptyIcon}>📜</Text>
            <Text style={styles.emptyTitle}>No scripts yet</Text>
            <Text style={styles.emptyDesc}>Create a script for your landing page to start capturing events</Text>
            <Button label="Create Script" onPress={openCreateModal} style={{ marginTop: 16 }} />
          </View>
        </Card>
      )}

      {/* Create / Edit Modal */}
      <Modal visible={modalVisible} title={editingScript ? 'Edit Script' : 'New Script'} onClose={() => { setModalVisible(false); setEditingScript(null); }}>
        <Input label="Name" value={form.name} onChangeText={(v) => setForm({ ...form, name: v })} placeholder="My Landing Page" />
        <Input label="Description" value={form.description} onChangeText={(v) => setForm({ ...form, description: v })} placeholder="Optional" />
        <Input
          label="Allowed Domains (one per line or comma-separated)"
          value={form.allowedDomains}
          onChangeText={(v) => setForm({ ...form, allowedDomains: v })}
          placeholder={"example.com\nshop.example.com"}
          multiline
        />
        <Text style={styles.hint}>Leave empty to accept events from any domain.</Text>
        <Button label={editingScript ? 'Save Changes' : 'Create'} onPress={handleSave} style={{ marginTop: 12 }} />
      </Modal>

      {/* Integration Modal */}
      <Modal visible={!!integrationModal} title="Integration Guide" onClose={() => setIntegrationModal(null)}>
        {integrationModal && (
          <View>
            {/* Step 1: Script tag */}
            <Text style={styles.stepTitle}>1. Add this script tag to your landing page</Text>
            <Text style={styles.stepDesc}>
              Place it in {'<head>'} BEFORE the standard Facebook Pixel code:
            </Text>
            <View style={styles.codeBlock}>
              <Text style={styles.codeText} selectable>{integrationModal.snippetTag}</Text>
            </View>
            <Button label="Copy Script Tag" icon="📋" size="sm" onPress={() => copyToClipboard(integrationModal.snippetTag)} style={{ marginTop: 8 }} />

            {/* Step 2: CSP */}
            <Text style={[styles.stepTitle, { marginTop: 24 }]}>2. Update Content-Security-Policy</Text>
            <Text style={styles.stepDesc}>
              If your site uses CSP headers, add these directives:
            </Text>
            <View style={styles.codeBlock}>
              <Text style={styles.codeText} selectable>{integrationModal.cspDirective}</Text>
            </View>
            <Button label="Copy CSP" icon="📋" size="sm" variant="ghost" onPress={() => copyToClipboard(integrationModal.cspDirective)} style={{ marginTop: 8 }} />

            {/* Step 3: Direct URL */}
            <Text style={[styles.stepTitle, { marginTop: 24 }]}>3. Script URL (reference)</Text>
            <View style={styles.codeBlock}>
              <Text style={styles.codeText} selectable>{integrationModal.snippetUrl}</Text>
            </View>
          </View>
        )}
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  cardHeader: { marginBottom: 12 },
  cardTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 4 },
  cardTitle: { fontSize: 18, fontWeight: '700', color: colors.white },
  cardDesc: { fontSize: 13, color: colors.textSecondary, marginTop: 2 },
  domainsRow: {
    flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap',
    marginBottom: 12, paddingBottom: 12, borderBottomWidth: 1, borderBottomColor: colors.border,
  },
  domainsLabel: { fontSize: 12, color: colors.textMuted, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.5 },
  anyDomain: { fontSize: 12, color: colors.textMuted, fontStyle: 'italic' },
  statsRow: { flexDirection: 'row', gap: 24, marginBottom: 16 },
  stat: {},
  statValue: { fontSize: 20, fontWeight: '700', color: colors.primary },
  statLabel: { fontSize: 11, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 },
  cardActions: { flexDirection: 'row', gap: 8, paddingTop: 12, borderTopWidth: 1, borderTopColor: colors.border },
  empty: { alignItems: 'center', paddingVertical: 40 },
  emptyIcon: { fontSize: 48, marginBottom: 12 },
  emptyTitle: { fontSize: 18, fontWeight: '700', color: colors.white, marginBottom: 4 },
  emptyDesc: { fontSize: 14, color: colors.textSecondary },
  hint: { fontSize: 12, color: colors.textMuted, marginTop: 4 },
  stepTitle: { fontSize: 15, fontWeight: '700', color: colors.white, marginBottom: 4 },
  stepDesc: { fontSize: 13, color: colors.textSecondary, marginBottom: 8 },
  codeBlock: { backgroundColor: colors.bg, borderRadius: radius.sm, padding: 16, borderWidth: 1, borderColor: colors.border },
  codeText: { fontFamily: 'monospace', fontSize: 13, color: colors.success, userSelect: 'all' },
});
